import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APIRoutes } from '../global-misc/api-routes';
import { MeModel as MeModel } from '../global-data-models/me.model';
import { firstValueFrom, BehaviorSubject, Observable } from 'rxjs';
import { GuardHelper } from '../global-guards/guard.helper';
import { OrgService } from './org.service';
import { OrgModel } from '../global-data-models/org.model';
import { Misc } from '../global-misc/misc';
import { CountryModel } from '../global-data-models/country.model';
import { CountryService } from './country.service';
import { LocaleService } from './locale.service';

@Injectable({ providedIn: 'root' })
export class MeService {
  private meSubject = new BehaviorSubject<MeModel | null>(null);
  public me$: Observable<MeModel | null> = this.meSubject.asObservable();

  constructor(
    private http: HttpClient,
    private readonly orgService: OrgService,
    private readonly localeService: LocaleService,
  ) {}

  getLastMeEmitted(): MeModel | null {
    return this.meSubject.value;
  }

  async getMe(): Promise<MeModel | null> {
    const firebaseUser = await GuardHelper.getCurrentUser();
    if (firebaseUser === null) {
      return null;
    }

    const apiRoute = APIRoutes.ME_ROUTE;
    const meModel = await firstValueFrom(this.http.get<MeModel>(apiRoute));
    const currentOrgId = this.getCurrentOrgId(meModel);

    meModel.currentOrganisationId = currentOrgId;

    if (currentOrgId) {
      try {
        const org: OrgModel = await this.orgService.getOrg(currentOrgId);
        meModel.currentOrganisationLocale = org.country.iso2code === 'US' ? 'en-US' : 'en-GB';
      } catch (error: unknown) {
        const msg: string = Misc.getErrorMessage(error);
        console.error(msg);
      }
    } else {
      console.warn('WARNING! currentOrgId undefined in getMe() so we could not fetch the current org Locale');
    }

    this.localeService.setLocale(meModel.currentOrganisationLocale ?? 'en-GB');
    console.log('Current locale set to ', this.localeService.getCurrentLocale());

    this.meSubject.next(meModel);
    return meModel;
  }

  async deleteMe(): Promise<void> {
    const apiRoute = APIRoutes.ME_ROUTE;
    await firstValueFrom(this.http.delete<void>(apiRoute));
    this.meSubject.next(null);
  }

  async changeCurrentOrgId(newOrgId: string, me: MeModel): Promise<void> {
    const settingsKey = me.id + '_current_org';
    localStorage.setItem(settingsKey, newOrgId);

    await this.refreshMe();
  }

  private getCurrentOrgId(me: MeModel): string | null {
    const settingsKey = me.id + '_current_org';
    const storedOrgId = localStorage.getItem(settingsKey);

    const allOrganisationsIds = me.userOrgPermissions.map((uop) => uop.organisation?.id);
    const validOrganisations = allOrganisationsIds.filter((orgId) => orgId !== null && orgId !== undefined);

    if (validOrganisations!.length === 0 && !storedOrgId) {
      return null;
    }

    if (validOrganisations!.length > 0 && !storedOrgId) {
      localStorage.setItem(settingsKey, validOrganisations[0]!);
      return validOrganisations[0]!;
    }

    const isValidstoredOrgId = validOrganisations!.includes(storedOrgId!);

    if (!isValidstoredOrgId && validOrganisations!.length === 0) {
      return null;
    }

    if (!isValidstoredOrgId && validOrganisations!.length > 0) {
      localStorage.setItem(settingsKey, validOrganisations[0]!);
      return validOrganisations[0]!;
    }

    return storedOrgId;
  }

  async refreshMe(): Promise<MeModel | null> {
    return await this.getMe();
  }

  static extractAllOrgs(meModel: MeModel | null) {
    if (meModel === null) {
      return [];
    }

    const uops = meModel.userOrgPermissions.map((uop) => {
      return uop;
    });

    const allUopsWithId = uops.filter((uop) => {
      return uop.organisation?.id !== null && uop.organisation?.id !== undefined;
    });

    const uniqueUops: {
      permission: {
        id: number;
        name: string;
      };
      organisation?: {
        id: string;
        legalName: string;
        displayName: string;
      };
    }[] = [];

    allUopsWithId.forEach((uop) => {
      const existingUop = uniqueUops.find((uniqueUop) => {
        return uniqueUop.organisation?.id === uop.organisation?.id;
      });

      if (!existingUop) {
        uniqueUops.push(uop);
      }
    });

    return uniqueUops.map((uop) => {
      return uop.organisation!;
    });
  }

  static extractCurrentOrgName(meModel: MeModel | null): string | null {
    if (meModel === null) {
      return null;
    }
    const currentOrgId = meModel.currentOrganisationId;
    if (!currentOrgId) {
      return null;
    }
    const currentOrg = meModel.userOrgPermissions.find((uop) => {
      return uop.organisation?.id === currentOrgId;
    });

    if (!currentOrg) {
      return null;
    }
    return currentOrg.organisation?.displayName || null;
  }

  static isOrgAdminUser(meModel: MeModel | null): boolean {
    if (meModel === null || !meModel.currentOrganisationId) {
      return false;
    }

    const currentOrgId = meModel.currentOrganisationId;

    const orgPermissions = meModel.userOrgPermissions.filter((uop) => {
      const isPartOfOrg = uop.organisation?.id === currentOrgId;
      const isOrgAdmin = uop.permission.name === 'ORG_ADMIN';
      return isPartOfOrg && isOrgAdmin;
    });

    return orgPermissions.length > 0;
  }

  static isSystemAdminUser(meModel: MeModel | null): boolean {
    if (meModel === null) {
      return false;
    }

    const sysAdminPermissions = meModel.userOrgPermissions.filter((permission) => {
      return permission.permission.name === 'SYSTEM_ADMIN';
    });

    return sysAdminPermissions.length > 0;
  }
}
